import { useMemo } from "react";
import { format } from "date-fns";
import { Rocket, Users, CalendarRange } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HasAccess } from "@/components/common/has-access";
import { toast } from "@/hooks/use-toast";
import {
  TimelineBundle,
  TimelineOffering,
} from "@/features/bundles/services/get-pricing-timeline";
import { useFullRolloutBundlesBulk } from "@/features/bundles/hooks/use-pricing-lifecycle";

const fmtDate = (iso: string | number | Date) => format(new Date(iso), "dd MMM yyyy");
const fmtMoney = (currency: string, n: number) =>
  `${currency} ${n.toLocaleString()}`;

// The price version an offering resolves to for a cohort whose window STARTS at
// `anchorMs` — mirrors the server's pickPriceVersion: the latest release with
// released_at <= anchor, else the baseline. `-Infinity` → baseline (pre-first-release).
function resolveAt(
  offering: TimelineOffering,
  anchorMs: number,
): TimelineBundle | null {
  const eligible = offering.experimental
    .filter(
      (e) => e.released_at && new Date(e.released_at).getTime() <= anchorMs,
    )
    .sort(
      (a, b) =>
        new Date(b.released_at as string).getTime() -
        new Date(a.released_at as string).getTime(),
    );
  return eligible[0] ?? offering.baseline ?? null;
}

type CohortRow = {
  offering: TimelineOffering;
  version: TimelineBundle | null;
  isExperiment: boolean;
};

type Cohort = {
  key: string;
  label: string; // human window, e.g. "15 Jul 2026 – 18 Jul 2026"
  isBaseline: boolean;
  startMs: number;
  rows: CohortRow[];
  rolloutIds: string[]; // one resolved version per offering (deduped)
};

export default function CohortTimelineView({
  offerings,
}: {
  offerings: TimelineOffering[];
}) {
  const bulkRollout = useFullRolloutBundlesBulk();

  const cohorts = useMemo<Cohort[]>(() => {
    // Union of all release boundaries across every offering of the product.
    const boundarySet = new Set<number>();
    offerings.forEach((o) =>
      o.experimental.forEach((e) => {
        if (e.released_at) boundarySet.add(new Date(e.released_at).getTime());
      }),
    );
    const boundaries = [...boundarySet].sort((a, b) => a - b);

    const build = (startMs: number, endMs: number | null, isBaseline: boolean) => {
      const rows: CohortRow[] = offerings.map((o) => {
        const version = resolveAt(o, isBaseline ? -Infinity : startMs);
        return {
          offering: o,
          version,
          isExperiment: !!version?.released_at,
        };
      });
      const rolloutIds = [
        ...new Set(
          rows
            .map((r) => r.version?.id)
            .filter((id): id is string => !!id),
        ),
      ];
      const label = isBaseline
        ? boundaries.length
          ? `Signed up before ${fmtDate(boundaries[0])}`
          : "Everyone (current baseline)"
        : endMs
          ? `${fmtDate(startMs)} – ${fmtDate(endMs)}`
          : `${fmtDate(startMs)} onwards`;
      return {
        key: isBaseline ? "baseline" : String(startMs),
        label,
        isBaseline,
        startMs,
        rows,
        rolloutIds,
      };
    };

    const list: Cohort[] = [];
    // Newest window first, baseline last (matches the offering view's ordering).
    for (let i = boundaries.length - 1; i >= 0; i--) {
      list.push(build(boundaries[i], boundaries[i + 1] ?? null, false));
    }
    list.push(build(-Infinity, boundaries[0] ?? null, true));
    return list;
  }, [offerings]);

  const handleGroupRollout = (cohort: Cohort) => {
    if (!cohort.rolloutIds.length) return;
    const msg = cohort.isBaseline
      ? `Roll the BASELINE prices out to everyone? This retires every experimental price across ${cohort.rows.length} offering(s).`
      : `Roll out the "${cohort.label}" cohort prices to EVERYONE? These become the production prices across ${cohort.rolloutIds.length} offering(s) and retire the other versions.`;
    if (!window.confirm(msg)) return;
    bulkRollout.mutate(cohort.rolloutIds, {
      onSuccess: () => toast({ title: "Cohort prices rolled out" }),
      onError: (e: unknown) =>
        toast({
          title: "Error",
          description:
            (e as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || "Group full rollout failed",
          variant: "destructive",
        }),
    });
  };

  if (!offerings.length) return null;

  return (
    <div className="space-y-4">
      {cohorts.map((cohort) => (
        <div
          key={cohort.key}
          className="rounded-lg border border-gray-200 bg-white overflow-hidden"
        >
          <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 bg-gray-50/60">
            <CalendarRange className="h-4 w-4 text-gray-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {cohort.label}
              </p>
              <p className="text-[11px] text-gray-500">
                {cohort.rows.length} offering
                {cohort.rows.length === 1 ? "" : "s"} ·{" "}
                {cohort.isBaseline
                  ? "current production prices"
                  : `${cohort.rolloutIds.length} price version(s)`}
              </p>
            </div>
            {cohort.isBaseline ? (
              <Badge variant="outline" className="ml-auto text-[10px] shrink-0">
                Baseline
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="ml-auto text-[10px] border-amber-500 text-amber-700 shrink-0"
              >
                Cohort
              </Badge>
            )}
            <HasAccess permission="write">
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                disabled={!cohort.rolloutIds.length || bulkRollout.isPending}
                onClick={() => handleGroupRollout(cohort)}
              >
                <Rocket className="h-3.5 w-3.5 mr-1" /> Full rollout
              </Button>
            </HasAccess>
          </div>

          <div className="divide-y divide-gray-100">
            {cohort.rows.map((row) => (
              <div
                key={row.offering.standard_id}
                className="flex items-center gap-3 px-5 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">
                    {row.offering.name}
                  </p>
                  <p className="text-[11px] font-mono text-gray-400 truncate">
                    {row.offering.standard_id}
                  </p>
                </div>
                {row.version?.cohort_size != null && !cohort.isBaseline && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-gray-400 shrink-0">
                    <Users className="h-3 w-3" />
                    {row.version.cohort_size.toLocaleString()}
                  </span>
                )}
                <Badge
                  variant="outline"
                  className={
                    row.isExperiment
                      ? "text-[10px] border-amber-500 text-amber-700 shrink-0"
                      : "text-[10px] shrink-0"
                  }
                >
                  {row.isExperiment ? "Experiment" : "Baseline"}
                </Badge>
                <span className="text-sm font-semibold text-gray-900 shrink-0 w-28 text-right">
                  {row.version
                    ? fmtMoney(row.offering.currency, row.version.amount)
                    : "—"}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
